import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Sparkles, Package, RefreshCw, Wand2, Target, Lightbulb, Users,
  HelpCircle, Network, FileJson, Terminal, Tag, GitBranch, Store, ChevronRight,
} from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { useStore } from "@/context/StoreProvider";
import { useAuth } from "@/context/AuthProvider";
import AiScore from "@/components/common/AiScore";
import KnowledgeGraphView from "@/components/common/KnowledgeGraphView";
import CodeBlock from "@/components/common/CodeBlock";

function Panel({ icon: Icon, title, children, testid }) {
  return (
    <section className="rounded-lg border border-border bg-card p-5" data-testid={testid}>
      <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-4 w-4 text-brand" />{title}
      </h3>
      {children}
    </section>
  );
}

function Bullets({ items }) {
  return (
    <ul className="space-y-2">
      {items?.map((it, i) => (
        <li key={i} className="flex gap-2 text-sm leading-relaxed">
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-brand" /><span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

export default function AISection() {
  const t = useT();
  const { store } = useStore();
  const { checkAuth } = useAuth();
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [provider, setProvider] = useState("live");

  const load = useCallback(async () => {
    if (!store) { setLoading(false); return; }
    try {
      const [{ data: prods }, { data: prov }] = await Promise.all([
        api.get("/products", { params: { store_id: store.id } }),
        api.get("/ai/provider"),
      ]);
      setProducts(prods);
      setProvider(prov.mode);
      if (prods.length) {
        setSelected(prods[0]);
        setInsights(prods[0].ai || null);
      }
    } finally {
      setLoading(false);
    }
  }, [store]);

  useEffect(() => { load(); }, [load]);

  const selectProduct = (p) => {
    setSelected(p);
    setInsights(p.ai || null);
  };

  const generate = async () => {
    if (!selected) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/ai/products/${selected.id}/generate`);
      setInsights(data);
      setProducts((ps) => ps.map((p) => (p.id === selected.id ? { ...p, ai: data } : p)));
      toast.success(t("ai.generatedWith", { provider: data.provider_used || "AI" }));
      checkAuth();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) toast.error(t("ai.insufficientCredits"));
      else if (status === 502) toast.error(t("ai.aiFailed"));
      else toast.error(t("ai.error"));
    } finally {
      setGenerating(false);
    }
  };

  const generateAll = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/ai/stores/${store.id}/generate-all`);
      toast.success(t("ai.generatedAll", { count: data.generated }));
      checkAuth();
      await load();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) toast.error(t("ai.insufficientCredits"));
      else toast.error(t("ai.error"));
    } finally {
      setGenerating(false);
    }
  };

  if (!store) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <Store className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="mt-4 text-muted-foreground">{t("dashboard.home.noStore")}</p>
        <Link to="/dashboard/store" className="mt-4 inline-flex rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground">{t("dashboard.home.noStoreCta")}</Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold tracking-tighter">
            <Sparkles className="h-6 w-6 text-brand" />{t("ai.title")}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t("ai.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs" data-testid="ai-mode-badge">
            {t("ai.mode")}: <span className="font-mono font-medium text-brand">{provider}</span>
            {insights?.provider_used && <span className="text-muted-foreground">{" · "}{insights.provider_used}{insights.fallback_used ? " ⤳" : ""}</span>}
          </span>
          {products.length > 0 && (
            <button onClick={generateAll} disabled={generating} data-testid="generate-all-btn"
              className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary disabled:opacity-60">
              <RefreshCw className={`h-4 w-4 ${generating ? "animate-spin" : ""}`} />{t("ai.generateAll")}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="mt-10 h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />
      ) : products.length === 0 ? (
        <div className="mt-10 rounded-lg border border-dashed border-border py-16 text-center">
          <Package className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">{t("ai.noProducts")}</p>
          <Link to="/dashboard/products" className="mt-4 inline-flex rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground">{t("products.add")}</Link>
        </div>
      ) : (
        <div className="mt-8 grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
          {/* Product selector */}
          <aside className="space-y-1.5">
            {products.map((p) => (
              <button key={p.id} onClick={() => selectProduct(p)} data-testid={`ai-select-${p.id}`}
                className={`flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors ${selected?.id === p.id ? "border-brand bg-brand/5" : "border-border hover:bg-secondary/50"}`}>
                <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md border border-border bg-secondary">
                  {p.images?.[0] ? <img src={p.images[0]} alt="" className="h-full w-full object-cover" /> : <Package className="m-2 h-5 w-5 text-muted-foreground" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.ai ? <span className="font-mono text-brand">AI {p.ai.ai_score}</span> : t("products.notScored")}</p>
                </div>
              </button>
            ))}
          </aside>

          {/* Insights */}
          <div className="min-w-0">
            {!insights ? (
              <div className="rounded-lg border border-dashed border-border py-16 text-center" data-testid="ai-not-generated">
                <Wand2 className="mx-auto h-10 w-10 text-muted-foreground" />
                <p className="mt-4 text-muted-foreground">{t("ai.notGenerated")}</p>
                <button onClick={generate} disabled={generating} data-testid="generate-btn"
                  className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-medium text-primary-foreground transition-transform hover:opacity-90 active:scale-95 disabled:opacity-60">
                  <Sparkles className="h-4 w-4" />{generating ? t("ai.generating") : t("ai.generate")}
                </button>
              </div>
            ) : (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="space-y-6" key={selected?.id}>
                {/* Header row: score + regenerate */}
                <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border bg-primary p-5 text-primary-foreground">
                  <div className="flex items-center gap-4">
                    <AiScore score={insights.ai_score} size="md" />
                    <div>
                      <h2 className="font-display text-lg font-bold">{selected.name}</h2>
                      <p className="text-sm text-primary-foreground/60">{selected.brand} · {selected.category}</p>
                    </div>
                  </div>
                  <button onClick={generate} disabled={generating} data-testid="regenerate-btn"
                    className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground transition-transform hover:opacity-90 active:scale-95 disabled:opacity-60">
                    <RefreshCw className={`h-4 w-4 ${generating ? "animate-spin" : ""}`} />{t("ai.regenerate")}
                  </button>
                </div>

                {/* Score breakdown */}
                <Panel icon={Target} title={t("ai.scoreBreakdown")} testid="panel-score">
                  <div className="grid gap-2 sm:grid-cols-2">
                    {insights.score_breakdown?.map((b, i) => (
                      <div key={i} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                        <span className={b.passed ? "" : "text-muted-foreground"}>{b.label}</span>
                        <span className={`font-mono ${b.passed ? "text-success" : "text-muted-foreground"}`}>{b.points}/{b.max}</span>
                      </div>
                    ))}
                  </div>
                </Panel>

                <Panel icon={Wand2} title={t("ai.aiDescription")} testid="panel-description">
                  <p className="text-sm leading-relaxed">{insights.ai_description}</p>
                </Panel>

                <div className="grid gap-6 md:grid-cols-2">
                  <Panel icon={Lightbulb} title={t("ai.problemsSolved")} testid="panel-problems"><Bullets items={insights.problems_solved} /></Panel>
                  <Panel icon={Package} title={t("ai.useCases")} testid="panel-usecases"><Bullets items={insights.use_cases} /></Panel>
                </div>

                <Panel icon={Users} title={t("ai.targetAudience")} testid="panel-target">
                  <div className="flex flex-wrap gap-2">
                    {insights.target_audience?.map((tg, i) => (
                      <span key={i} className="rounded-full border border-border bg-secondary/50 px-3 py-1 text-sm">{tg}</span>
                    ))}
                  </div>
                </Panel>

                <Panel icon={HelpCircle} title={t("ai.faq")} testid="panel-faq">
                  <div className="space-y-3">
                    {insights.faq?.map((f, i) => (
                      <div key={i} className="rounded-md border border-border p-3">
                        <p className="text-sm font-semibold">{f.q}</p>
                        <p className="mt-1 text-sm text-muted-foreground">{f.a}</p>
                      </div>
                    ))}
                  </div>
                </Panel>

                <Panel icon={Network} title={t("ai.knowledgeGraph")} testid="panel-graph">
                  <KnowledgeGraphView graph={insights.knowledge_graph} />
                </Panel>

                <Panel icon={Tag} title={t("ai.semanticKeywords")} testid="panel-keywords">
                  <div className="flex flex-wrap gap-2">
                    {insights.semantic_keywords?.map((k, i) => (
                      <span key={i} className="rounded-md bg-brand/10 px-2.5 py-1 font-mono text-xs text-brand">{k}</span>
                    ))}
                  </div>
                </Panel>

                <Panel icon={GitBranch} title={t("ai.correlations")} testid="panel-correlations">
                  <div className="space-y-2">
                    {insights.correlations?.map((c, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between text-sm">
                          <span>{c.label}</span><span className="font-mono text-xs text-muted-foreground">{Math.round(c.strength * 100)}%</span>
                        </div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-border">
                          <div className="h-full rounded-full bg-brand" style={{ width: `${c.strength * 100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </Panel>

                <Panel icon={FileJson} title={t("ai.jsonLd")} testid="panel-jsonld">
                  <CodeBlock code={insights.json_ld} language="json-ld" testid="jsonld-block" />
                </Panel>

                <Panel icon={Terminal} title={t("ai.promptPreview")} testid="panel-prompt">
                  <CodeBlock code={insights.prompt_preview} language="prompt" testid="prompt-block" />
                </Panel>
              </motion.div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
